// Third Party
import * as React from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import ReactDOM from 'react-dom';
import { parseCookies, setCookie } from 'nookies';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';

// Utils
import { useTimeoutFn } from '@hooks';
import { routes } from '@routes';

// UI
import { Typography } from '@components/typography';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { IconNames } from '@components/icon';
import vectorArt from '../../../public/assets/svg/illustrations/beach_chairs.svg';
import Image from 'next/image';

type Props = {};

const HIDE_MODAL_KEY = '__savvy_should_hide_middleman_modal__';

const ALLOWED_PATHS = [
  routes.home,
  routes.search,
  '/vacation-rentals', // Base path for SERP pages
];

function isModalHidden(): boolean {
  const cookies = parseCookies();

  return cookies[HIDE_MODAL_KEY] === 'true';
}

function persistHideModal(): void {
  setCookie(null, HIDE_MODAL_KEY, 'true', {
    maxAge: 48 * 60 * 60, // 48 hours (2 days)
    path: '/',
  });
}

function isAllowedPage(pathname: string): boolean {
  // Check if we're on an allowed page
  return ALLOWED_PATHS.some(
    (allowedPath) =>
      pathname === allowedPath || // Exact match for home and search
      (allowedPath === '/vacation-rentals' &&
        pathname.startsWith('/vacation-rentals/')) // SERP pages
  );
}

const CreateAccountPopupPrivate: React.FC<Props> = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { data: session, status } = useSession();
  const router = useRouter();

  const isAuthenticated = status === 'authenticated' && !!session;

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modalActive');
    } else {
      document.body.classList.remove('modalActive');
    }
  }, [isModalOpen]);

  useTimeoutFn(() => {
    // Only show modal if:
    // 1. User is not logged in
    // 2. Modal hasn't been hidden
    // 3. We're on an allowed page
    if (
      !isModalHidden() &&
      !isAuthenticated &&
      isAllowedPage(router.pathname)
    ) {
      setIsModalOpen(true);
    }
  }, 7000); // Changed from 500 to 7000 (7 seconds)

  const dismissModalHandler = React.useCallback(() => {
    persistHideModal();
    setIsModalOpen(false);
  }, []);

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  const closeModal = (): void => {
    dismissModalHandler();
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [isModalOpen]);

  // If user logs in while modal is open, close it
  React.useEffect(() => {
    if (isAuthenticated && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [isAuthenticated, isModalOpen]);

  return ReactDOM.createPortal(
    <Transition
      show={isModalOpen}
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: 9999 }}
      as="div"
    >
      <div className="block items-end justify-center md:mx-5 min-h-screen text-center">
        <TransitionChild
          as="div"
          enter="ease-out duration-400 md:duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 transition-opacity">
            <div
              onClick={() => {
                setIsModalOpen(false);
              }}
              className="absolute inset-0 bg-black opacity-80"
            />
          </div>
        </TransitionChild>

        <TransitionChild
          className="inline-block text-left bg-primary-xlight rounded-2xl shadow-xl transform transition-all align-middle w-full md:w-[500px] relative overflow-hidden mt-[50px] sm:mt-40 border-2 border-primary-main"
          as="div"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          enter="ease-out duration-400 md:duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div className="flex flex-col p-6 text-left">
            {/* Main content */}
            <div className="mb-8 border-[2px] border-primary-main px-4 rounded-lg relative space-y-32">
              <div className="flex flex-col justify-between gap-4 py-2">
                <div className="flex flex-row justify-end mr-4 absolute top-1 sm:top-2 -right-4 z-10">
                  <ButtonIcon
                    ariaDescribedby="close create account popup"
                    icon={IconNames.X}
                    onClick={closeModal}
                    variant="text"
                    size="md"
                  />
                </div>
                <Typography
                  variant="title1"
                  component="h1"
                  className="text-primary-main text-2xl sm:text-3xl"
                >
                  Create a Savvy account.
                </Typography>

                <Typography
                  variant="title1"
                  component="h1"
                  className="text-primary-main text-2xl sm:text-3xl"
                >
                  Get $50 cash back on your first stay.
                </Typography>

                <Typography
                  variant="title1"
                  component="h1"
                  className="text-primary-main text-2xl sm:text-3xl"
                >
                  It only takes a minute.
                </Typography>
              </div>

              <div className="flex flex-row justify-between py-2">
                <Typography
                  component="p"
                  variant="body1"
                  className="text-primary-main mt-6 text-md md:text-xl"
                >
                  Stay smarter.
                </Typography>

                <Image
                  src={vectorArt}
                  alt="Beach chairs"
                  className="absolute -bottom-[0.05rem] right-2 w-40 sm:w-[200px]"
                />
              </div>
            </div>

            {/* Button */}
            <div className="mb-4 w-2/3 min-w-[200px] mx-auto ">
              <Button
                color="primary"
                variant="filled"
                size="lg"
                className="w-full rounded-full justify-center min-w-[200px]"
                onClick={() => {
                  window.location.href = routes.createAccount;
                  closeModal();
                }}
              >
                Create Your Account
              </Button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Transition>,
    document.getElementById('modal-root') as Element
  );
};

export default CreateAccountPopupPrivate;
